// Всплывающая форма заявки на акционный товар. Открывается кнопкой «Заказать»
// главного баннера, когда блок настроен на режим «форма» (корзина при этом не
// участвует — лендингу она не нужна).
//
// Шелл диалога (.dialog / .dialog__bg / .dialog__content / .dialog__close) и
// поля (.ng-fast-order__*) уже описаны в стилях всех шаблонов семейства newgen,
// свои классы только у строки товара (.ng-fast-order__item*).
// Сабмит — тот же /api/fast_order, что у блока «Форма», плюс товар и количество.
// Состав формы задаётся настройками блока (withName / withQuantity / sentText):
// по умолчанию спрашиваем только телефон, количество берём из настроек блока.

import React from 'react';
import InputMask from 'react-input-mask';
import { observer } from '../../shared/observer';
import { minOrderCount, maxOrderCount, initialOrderCount } from '../../shared/orderCount';
import { unitPriceForQuantity } from '../../shared/price';
import { curFormat } from '../../shared/format';

const csrfToken = () => {
  const m = document.querySelector("meta[name='csrf-token']");
  return m ? m.getAttribute('content') : '';
};

class FastOrderDialogView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      phone: '',
      quantity: this.startQuantity(props),
      sending: false,
      sent: false,
      error: null,
    };
  }

  // Стартовое количество — как у кнопки «В корзину»: количество из настроек
  // блока, но не ниже минимального и не выше остатка на складе.
  startQuantity(props) {
    const params = props.params || {};
    return Math.min(
      Math.max(initialOrderCount(params), parseInt(params.quantity, 10) || 0),
      maxOrderCount(params),
    );
  }

  setField = (field) => (e) => {
    this.setState({ [field]: e.target.value, error: null });
  };

  inc = () => {
    const params = this.props.params || {};
    this.setState((s) => ({ quantity: Math.min(s.quantity + 1, maxOrderCount(params)) }));
  };

  dec = () => {
    const params = this.props.params || {};
    this.setState((s) => ({ quantity: Math.max(s.quantity - 1, minOrderCount(params)) }));
  };

  close = () => {
    if (this.state.sending) return;
    // после успешной отправки диалог открывается заново уже пустым
    if (this.state.sent) this.setState({ sent: false, quantity: this.startQuantity(this.props) });
    if (this.props.onClose) this.props.onClose();
  };

  submit = async (e) => {
    e.preventDefault();
    // Анти-дабл-сабмит: повторный submit (Enter) до ре-рендера disabled игнорируем.
    if (this.state.sending) return;
    const params = this.props.params || {};
    const phoneDigits = (this.state.phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      this.setState({ error: 'Укажите телефон' });
      return;
    }
    this.setState({ sending: true, error: null });
    try {
      const resp = await fetch('/api/fast_order', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          name: this.state.name,
          phone: this.state.phone,
          title: params.title || params.itemTitle || '',
          item_id: params.itemId || '',
          quantity: this.state.quantity,
          page_url: window.location.href,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.status === 'ok') {
        this.setState({ sent: true, name: '', phone: '', sending: false });
        if (params.js) {
          try { eval(params.js); } catch (err) { /* noop */ }
        }
      } else {
        this.setState({ error: 'Не удалось отправить заявку', sending: false });
      }
    } catch (err) {
      this.setState({ error: 'Не удалось отправить заявку', sending: false });
    }
  };

  render() {
    if (!this.props.open) return null;
    const params = this.props.params || {};
    const auth = this.props.auth || {};
    const mask = (auth.phoneMaskInfo && auth.phoneMaskInfo.phone_mask) || '+9 (999) 999-99-99';
    const atMax = this.state.quantity >= maxOrderCount(params);
    const atMin = this.state.quantity <= minOrderCount(params);
    const currency = (auth.companyInfo && auth.companyInfo.currency_hash
      && auth.companyInfo.currency_hash.symbol_html) || '';
    const total = unitPriceForQuantity(params, this.state.quantity) * this.state.quantity;
    const submitLabel = params.submitLabel || 'Отправить заявку';
    // Состав формы — настройки блока баннера (см. WHelper#hero_form_params):
    // имя по умолчанию не спрашиваем, товар со счётчиком показываем.
    const withName = params.withName === true;
    const withQuantity = params.withQuantity !== false;
    const sentText = params.sentText || 'Спасибо! Скоро с вами свяжется наш менеджер.';

    return (
      <div className="dialog ng-fast-order-dialog">
        <div className="dialog__bg" onClick={this.close} />
        <div className="dialog__content">
          <div className="dialog__close" onClick={this.close} />
          <div className="h1">{params.dialogTitle || 'Оформить заказ'}</div>
          {this.state.sent ? (
            <div className="ng-fast-order__sent">{sentText}</div>
          ) : (
            <form className="ng-fast-order__form" onSubmit={this.submit}>
              {params.itemTitle && withQuantity ? (
                <div className="ng-fast-order__item">
                  <span className="ng-fast-order__item-title">{params.itemTitle}</span>
                  <span className="item-show-price-incart__wrapper">
                    <a
                      href="#"
                      className={'down' + (atMin ? ' is-disabled' : '')}
                      aria-disabled={atMin ? 'true' : null}
                      onClick={(e) => { e.preventDefault(); this.dec(); }}
                    >−</a>
                    <span className="item-show-price-incart__count">{this.state.quantity}</span>
                    <a
                      href="#"
                      className={'up' + (atMax ? ' is-disabled' : '')}
                      aria-disabled={atMax ? 'true' : null}
                      onClick={(e) => { e.preventDefault(); this.inc(); }}
                    >+</a>
                  </span>
                </div>
              ) : null}
              {params.itemTitle && withQuantity && total > 0 ? (
                <div className="ng-fast-order__total">
                  <span>Сумма</span>
                  <span className="ng-fast-order__total-value">{curFormat(total)} {currency}</span>
                </div>
              ) : null}
              {withName ? (
                <div className="ng-fast-order__row">
                  <input
                    type="text"
                    placeholder="Имя"
                    value={this.state.name}
                    onChange={this.setField('name')}
                    disabled={this.state.sending}
                  />
                </div>
              ) : null}
              <div className="ng-fast-order__row">
                <InputMask
                  mask={mask}
                  value={this.state.phone}
                  onChange={this.setField('phone')}
                  type="tel"
                  placeholder="Телефон"
                  className="input__text"
                  disabled={this.state.sending}
                />
              </div>
              {this.state.error && (<div className="order-message">{this.state.error}</div>)}
              <div className="ng-fast-order__consent">
                Нажимая на кнопку «{submitLabel}», вы соглашаетесь с{' '}
                <a href="/conds" target="_blank" rel="noopener noreferrer">политикой конфиденциальности</a>
              </div>
              <div className="ng-fast-order__actions">
                <button type="submit" className="btn btn--lg" disabled={this.state.sending}>
                  {this.state.sending ? 'Отправляем…' : submitLabel}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }
}

export const FastOrderDialog = observer(FastOrderDialogView, ['authStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Pages/FastOrderDialog.jsx